// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuidv4 } from 'uuid';

import { getAppConfig } from './get-env-config';

/**
 * Performance.now is not supported in opera mini
 */
export const perfNow = (): number => {
	try {
		// eslint-disable-next-line
		return performance.now();
	} catch {
		return 0;
	}
};

export const createPerformanceObserver = (
	site: string | string[],
	entryTypes: PerformanceEntry['entryType'][],
): {
	takeEntriesAndDisconnect: () => PerformanceEntry[];
} => {
	const entries: PerformanceEntry[] = [];

	const handleEntry = (entry: PerformanceEntry) => {
		if (entry.entryType === 'resource') {
			const url = new URL(entry.name);
			if (
				(typeof site === 'string' && url.host === site) ||
				(Array.isArray(site) && site.includes(url.host))
			) {
				entries.push(entry);
			}
		} else {
			entries.push(entry);
		}
	};

	let observer: PerformanceObserver | null = null;

	try {
		observer = new PerformanceObserver((entries) => {
			entries.getEntries().forEach((entry) => {
				handleEntry(entry);
			});
		});

		for (const entryType of entryTypes) {
			observer.observe({
				type: entryType,
				buffered: true,
			});
		}

		return {
			takeEntriesAndDisconnect: () => {
				if (observer) {
					const records = observer.takeRecords();
					for (const record of records) {
						handleEntry(record);
					}
					observer.disconnect();
				}
				return entries;
			},
		};
	} catch {
		// If observer was created but observe() failed, clean it up
		if (observer) {
			try {
				observer.disconnect();
			} catch {
				// Ignore errors during cleanup
			}
		}

		// Fallback for browsers (Safari/WebKit) that don't support this pattern
		// Return a no-op implementation that returns an empty array
		return {
			takeEntriesAndDisconnect: () => [],
		};
	}
};

export const performanceAttributes = (
	entry: PerformanceEntry,
):
	| {
			resource: string;
			duration: number;
			entryType: string;
			startTime: number;
	  }
	| {
			tcpHandshakeDuration: number;
			domainLookupDuration: number;
			redirectionDuration: number;
			requestDuration: number;
			tlsNegotiationDuration: number;
			fetchDuration: number;
			resource: string;
			duration: number;
			entryType: string;
			startTime: number;
	  } => {
	const baseAttributes = {
		resource: entry.name,
		duration: entry.duration,
		entryType: entry.entryType,
		startTime: entry.startTime,
	};

	if (entry.entryType === 'resource') {
		const resourceEntry = entry as PerformanceResourceTiming;
		return {
			...baseAttributes,
			tcpHandshakeDuration: resourceEntry.connectEnd - resourceEntry.connectStart,
			domainLookupDuration: resourceEntry.domainLookupEnd - resourceEntry.domainLookupStart,
			redirectionDuration: resourceEntry.redirectEnd - resourceEntry.redirectStart,
			requestDuration: resourceEntry.responseStart - resourceEntry.requestStart,
			tlsNegotiationDuration: resourceEntry.requestStart - resourceEntry.secureConnectionStart,
			fetchDuration: resourceEntry.responseEnd - resourceEntry.fetchStart,
		};
	}

	return baseAttributes;
};

const STAGING_ANALYTICS_URL = 'https://as.staging.atl-paas.net/api/v1/batch';
const PROD_ANALYTICS_URL = 'https://as.atlassian.com/api/v1/batch';

export const sendPerformanceEntries = (
	entries: PerformanceEntry[],
	analyticsContext?: Record<string, unknown> | undefined,
): void => {
	const destination = getAppConfig().env === 'prod' ? PROD_ANALYTICS_URL : STAGING_ANALYTICS_URL;

	fetch(destination, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			batch: entries.slice(0, 10).map((entry) => ({
				type: 'track',
				event: 'performance measured',
				// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
				messageId: uuidv4(),
				timestamp: new Date().toISOString(),
				sentAt: new Date().toISOString(),
				properties: {
					eventType: 'operational',
					env: getAppConfig().env,
					source: 'script',
					tags: [],
					origin: 'web',
					platform: 'web',
					product: 'csm',
					subproduct: 'widget',
					action: 'measured',
					actionSubject: 'performance',
					attributes: {
						entry: entry.toJSON(),
						...performanceAttributes(entry),
						...(analyticsContext?.attributes ?? {}),
					},
				},
			})),
			context: {
				validationMode: 'complete',
			},
			sentAt: new Date().toISOString(),
		}),
	});
};
